// What the daily check-in popup has to say — worked out here, without React or
// i18n, so the figures it shows can be tested against the same day data the
// Rewards page renders. The popup used to say "Your daily check-in is ready"
// and nothing else; a player had no reason to tap. Now it leads with what
// today pays, where in the week they are, and what missing today costs.
import type { ActivityCheckin } from '../../features/profile/api';
import type { CheckinReward, RichCheckinDay } from '../profile/checkinDays';

export type DailyPopupTile = {
  day: RichCheckinDay;
  // done = already banked this streak; today = the one to claim; ahead = later
  state: 'done' | 'today' | 'ahead';
};

export type DailyPopupTrack = {
  track: string;
  streakDay: number;
  days: RichCheckinDay[];
  today: CheckinReward[];
};

export type DailyPopupModel = {
  // The track whose ladder the strip shows: the richest claimable one today.
  primary: DailyPopupTrack;
  // Every track that can be claimed right now — the headline sums them.
  claimable: DailyPopupTrack[];
  todayRewards: CheckinReward[];
  tiles: DailyPopupTile[];
  // Everything the primary ladder pays over 7 days (day-1 message).
  weekRewards: CheckinReward[];
  // What day 1 of the primary ladder pays — where a broken streak lands you.
  restartRewards: CheckinReward[];
  // Cash already sitting claimable in the reward inbox, on top of check-ins.
  inboxCash: number;
};

function rewardsOf(day: RichCheckinDay | undefined): CheckinReward[] {
  if (!day) return [];
  if (day.all?.length) return day.all;
  return [{ kind: day.kind, amount: day.amount, ticket_count: day.ticket_count, coupon_code: day.coupon_code }];
}

function funded(days: RichCheckinDay[]): boolean {
  return days.some((d) => Number(d.amount) > 0 || Number(d.ticket_count) > 0 || !!d.coupon_code);
}

// Cash outranks tickets outranks coupons when choosing which ladder to show;
// within a kind, more is better. Only used to pick the primary track.
function score(rewards: CheckinReward[]): number {
  const cash = rewards.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const tickets = rewards.reduce((sum, r) => sum + Number(r.ticket_count || 0), 0);
  const coupons = rewards.filter((r) => !!r.coupon_code).length;
  return cash * 1_000_000 + tickets * 1_000 + coupons;
}

export function clampStreakDay(value: number | undefined): number {
  return Math.max(1, Math.min(7, Number(value) || 1));
}

export function buildDailyPopupModel(input: {
  checkins: ActivityCheckin[] | undefined;
  trackDays: Map<string, RichCheckinDay[]>;
  inboxCash?: number;
}): DailyPopupModel | null {
  const claimable: DailyPopupTrack[] = [];
  for (const c of input.checkins || []) {
    if (c.active === false) continue;
    if (!c.eligibility?.eligible || c.claimed) continue;
    const days = input.trackDays.get(c.track) || [];
    if (!funded(days)) continue;
    const streakDay = clampStreakDay(c.streakDay);
    const today = rewardsOf(days.find((d) => d.day === streakDay));
    // A day with nothing behind it is not worth a popup, however eligible.
    if (score(today) === 0) continue;
    claimable.push({ track: c.track, streakDay, days, today });
  }
  if (claimable.length === 0) return null;
  const primary = [...claimable].sort((a, b) => score(b.today) - score(a.today))[0];
  const tiles: DailyPopupTile[] = primary.days.map((day) => ({
    day,
    state: day.day < primary.streakDay ? 'done' : day.day === primary.streakDay ? 'today' : 'ahead',
  }));
  return {
    primary,
    claimable,
    todayRewards: claimable.flatMap((tr) => tr.today),
    tiles,
    weekRewards: primary.days.flatMap((day) => rewardsOf(day)),
    restartRewards: rewardsOf(primary.days.find((d) => d.day === 1)),
    inboxCash: Math.max(0, Number(input.inboxCash) || 0),
  };
}
